import { Injectable, inject } from '@angular/core';
import {
    HttpClient,
    HttpParams,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { InputFileDetails, RecentInputFilesResponse, TransformationDetails } from '../models/input-file';


export interface DashboardSummary {
    received: number;
    processed: number;
    processing_failed: number;
    api_sent: number;
    api_retrying: number;
    api_failed: number;
    api_not_queued: number;
}


export interface ProcessingActivity {
    date: string;
    received: number;
    processed: number;
    failed: number;
}


export interface ApiDeliverySummary {
    sent: number;
    retrying: number;
    failed: number;
    not_queued: number;
}


export interface PipelineSummary {
    received: number;
    queued: number;
    processing: number;
    processed: number;
    failed: number;
    api_queued: number;
    api_retry: number;
    api_sent: number;
    api_failed: number;
}


export interface MarketDashboardItem {
    market_id: number;
    market_code: string;
    market_name: string;
    files: number;
    success_rate: number;
    status: string;
}


export interface RecentActivity {
    file_id: number;
    filename: string;
    event_type: string;
    message: string | null;
    created_at: string;
}


export interface RecentFailure {
    file_id: number;
    filename: string;
    stage: string;
    error_message: string | null;
    exception_type: string | null;
    created_at: string;
}


export interface DashboardResponse {
    summary: DashboardSummary;
    processing_activity: ProcessingActivity[];
    api_delivery: ApiDeliverySummary;
    pipeline: PipelineSummary;
    markets: MarketDashboardItem[];
    recent_activity: RecentActivity[];
    recent_failures: RecentFailure[];
}


@Injectable({
    providedIn: 'root',
})
export class DashboardService {

    private readonly http = inject(HttpClient);

    private readonly baseUrl = '/api/v1';

    getDashboard(
        companyId: number,
        marketId: number | null,
        days: number = 7,
    ): Observable<DashboardResponse> {
        let params = new HttpParams()
            .set('company_id', companyId)
            .set('days', days);

        if (marketId !== null) {
            params = params.set('market_id', marketId);
        }

        return this.http.get<DashboardResponse>(
            `${this.baseUrl}/dashboard`,
            { params },
        );
    }

    getRecentInputFiles(
        companyId: number,
        marketId: number | null,
        limit: number = 10,
        offset: number = 0
    ): Observable<RecentInputFilesResponse> {
        let params = new HttpParams()
            .set('company_id', companyId)

            .set('limit', limit)
            .set('offset', offset);

        if (marketId !== null) {
            params = params.set('market_id', marketId);
        }

        return this.http.get<RecentInputFilesResponse>(
            `${this.baseUrl}/input-files/recent`,
            { params }
        );
    }

    getInputFileDetails(
        fileId: number
    ): Observable<InputFileDetails> {
        return this.http.get<InputFileDetails>(
            `${this.baseUrl}/input-files/${fileId}/details`
        );
    }

    downloadInputFile(
        fileId: number,
    ): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/transformations/files/${fileId}/download`,
            {
                responseType: 'blob',
            },
        );
    }

    downloadOutputArchive(fileId: number): Observable<Blob> {
        return this.http.get(
            `${this.baseUrl}/transformations/files/${fileId}/output-archive/download`,
            { responseType: 'blob' },
        );
    }

    sendOutputArchiveToApi(fileId: number): Observable<void> {
        return this.http.post<void>(
            `${this.baseUrl}/input-files/${fileId}/output-archive/send-to-api`,
            {},
        );
    }

    reprocessFile(fileId: number) {
        return this.http.post<{ message: string; task_id: string }>(
            `${this.baseUrl}/input-files/${fileId}/reprocess`,
            {}
        );
    }
}